import { useTranslation } from "react-i18next";

import "./confirm-dialog.css";

import { Button } from "./button";
import type { ButtonTone } from "./button";
import { Dialog } from "./dialog";

// A controlled confirm popup on the Dialog primitive — scrim, focus-trap, and
// Esc/backdrop-dismiss come from Base UI. Replaces native window.confirm so
// destructive actions read in-app. The caller owns `open` and runs the async
// work in onConfirm (keep `busy` true while it runs; close on success).
// Dismissing (Esc / backdrop / Cancel) calls onCancel — suppressed while busy
// so an in-flight confirm isn't yanked out from under itself.
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ButtonTone;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("shared");
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <Dialog.Content className="rc-confirm">
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description>{message}</Dialog.Description>
        <div className="rc-confirm__actions">
          <Button variant="ghost" tone="neutral" disabled={busy} onClick={onCancel} type="button">
            {cancelLabel ?? t("confirmDialog.cancel")}
          </Button>
          <Button variant="solid" tone={tone} disabled={busy} onClick={onConfirm} type="button">
            {confirmLabel ?? t("confirmDialog.confirm")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
